import { h } from "../core/dom.js";
import { muscleName, getBodyMap } from "../store.js";

// Our muscle catalog (lowercase name) → SVG region slugs in the body-map art.
// (Art: react-muscle-highlighter, MIT — male & female front/back.)
export const muscleSlugs = {
  abdominals: ["abs", "obliques"],
  abductors: ["gluteal"],
  adductors: ["adductors"],
  biceps: ["biceps"],
  calves: ["calves"],
  chest: ["chest"],
  forearms: ["forearm"],
  glutes: ["gluteal"],
  hamstrings: ["hamstring"],
  lats: ["upper-back"],
  "lower back": ["lower-back"],
  "middle back": ["upper-back", "trapezius"],
  neck: ["neck"],
  quadriceps: ["quadriceps"],
  shoulders: ["deltoids"],
  traps: ["trapezius"],
  triceps: ["triceps"],
};

// Reverse map for selection-mode clicks (slug → canonical muscle name).
const slugToMuscle = {
  abs: "abdominals", obliques: "abdominals", biceps: "biceps", triceps: "triceps",
  neck: "neck", trapezius: "traps", deltoids: "shoulders", adductors: "adductors",
  quadriceps: "quadriceps", calves: "calves", forearm: "forearms", chest: "chest",
  gluteal: "glutes", hamstring: "hamstrings", "lower-back": "lower back", "upper-back": "lats",
};

const svgCache = {};
function loadSvg(url) {
  if (!svgCache[url]) svgCache[url] = fetch(url).then((r) => r.text());
  return svgCache[url];
}

const toName = (v) => (typeof v === "number" ? muscleName(v) : v);
const slugsFor = (name) => muscleSlugs[String(name).toLowerCase()] || [];

/**
 * Render the front+back body map for the user's selected sex.
 * options: primary[], secondary[], indirect[] (muscle names or ids), recovery:Map<name,days>,
 *          selectable, selected:Set<name>, onToggle(name),
 *          onRegionClick(name) — navigation-style tap on a region (ignored when selectable)
 */
export function MuscleMap(options = {}) {
  const container = h("div", { class: "muscle-map" });
  render(container, options);
  return container;
}

async function render(container, options) {
  const sex = getBodyMap();
  const [front, back] = await Promise.all([loadSvg(`/svgs/front-${sex}.svg`), loadSvg(`/svgs/back-${sex}.svg`)]);
  container.innerHTML = `<div class="muscle-map-side">${front}</div><div class="muscle-map-side">${back}</div>`;

  const groups = (slug) => container.querySelectorAll(`[id="${CSS.escape(slug)}"]`);
  const addClass = (names, cls) => {
    for (const name of names.map(toName).filter(Boolean)) {
      for (const slug of slugsFor(name)) groups(slug).forEach((g) => g.classList.add(cls));
    }
  };

  // "Indirect" = worked only as a secondary mover (never primary). Painted first so a
  // primary/recovery fill always wins if a muscle somehow qualifies for both.
  addClass(options.indirect || [], "mm-indirect");
  addClass(options.secondary || [], "mm-secondary");
  addClass(options.primary || [], "mm-primary");

  if (options.recovery) {
    for (const [name, days] of options.recovery) {
      const level = days <= 0 ? "0" : days === 1 ? "1" : days <= 3 ? "2" : days < 14 ? "3" : "4";
      for (const slug of slugsFor(name)) groups(slug).forEach((g) => g.classList.add(`mm-recovery-${level}`));
    }
  }

  const listButtons = new Map();
  if (options.selectable || options.onRegionClick) {
    const list = h("div", {class:"muscle-text-list"});
    container.append(h("details", {class:"muscle-map-alternative"}, h("summary",null,options.selectable ? "Choose muscles by name" : "View muscles by name"),list));
    for (const name of Object.keys(muscleSlugs)) {
      const days=options.recovery?.get(name);
      const detail=options.recovery ? days==null ? (options.indirect?.includes(name)?"indirect training":"no primary training logged") : days===0?"trained today":`${days} days since training` : "";
      const button=h("button", {class:"btn btn-ghost",type:"button",onClick:()=>{
        if(options.selectable){
          const selected=options.selected || (options.selected=new Set());
          selected.has(name)?selected.delete(name):selected.add(name);
          container._repaint?.();options.onToggle?.(name,selected);
        } else options.onRegionClick(name);
      }},name,detail?h("span",{class:"dim"},detail):null);
      listButtons.set(name,button);list.append(button);
    }
  }

  // Tap-to-navigate mode (e.g. the dashboard recovery map → per-muscle page). Distinct
  // from `selectable`, which toggles membership in a picker instead.
  if (options.onRegionClick && !options.selectable) {
    container.querySelectorAll(".mm-region").forEach((g) => {
      const muscle = slugToMuscle[g.id];
      if (!muscle) return;
      g.style.cursor = "pointer";
      g.addEventListener("click", () => options.onRegionClick(muscle));
    });
  }

  if (options.selectable) {
    const selected = options.selected || (options.selected = new Set());
    const paint = () => {
      listButtons.forEach((button,name) => button.setAttribute("aria-pressed",String(selected.has(name))));
      container.querySelectorAll(".mm-region").forEach((g) => g.classList.remove("mm-selected"));
      for (const name of selected) for (const slug of slugsFor(name)) groups(slug).forEach((g) => g.classList.add("mm-selected"));
    };
    paint();
    // Let callers that mutate `selected` externally (e.g. chip toggles) repaint the
    // body map without rebuilding it — avoids reloading/reflashing the SVG.
    container._repaint = paint;
    container.querySelectorAll(".mm-region").forEach((g) => {
      const muscle = slugToMuscle[g.id];
      if (!muscle) return;
      g.style.cursor = "pointer";
      g.addEventListener("click", () => {
        selected.has(muscle) ? selected.delete(muscle) : selected.add(muscle);
        paint();
        options.onToggle?.(muscle, selected);
      });
    });
  }
}
